import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPaymentFees } from '../../services/payment-services';

import type { PaymentMethodType, PaymentRow } from './usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { FeeResponse } from '../../services/payment-services';
import type { Dispatch, SetStateAction } from 'react';

interface StateRule {
    mode: 'STANDARD' | 'CAPPED' | 'ACH_ONLY';
    capPercent: number; // 0.03, 0.02, etc.
}

const STATE_RULES: Record<string, StateRule> = {
    CT: { mode: 'ACH_ONLY', capPercent: 0 },
    MA: { mode: 'ACH_ONLY', capPercent: 0 },
    ME: { mode: 'ACH_ONLY', capPercent: 0 },
    CA: { mode: 'ACH_ONLY', capPercent: 0 },
    KS: { mode: 'ACH_ONLY', capPercent: 0 },
    NY: { mode: 'ACH_ONLY', capPercent: 0 },
    CO: { mode: 'CAPPED', capPercent: 0.02 },
};
const DEFAULT_RULE: StateRule = { mode: 'STANDARD', capPercent: 0.03 };

function getStateRule(state: string): StateRule {
    return STATE_RULES[state?.trim().toUpperCase()] ?? DEFAULT_RULE;
}

interface UseCardFeesParams {
    userContractsCountryCombinations: Array<{ country: string; state: string }>;
    getContractById: (contractId: string) => ContractApiResponse | null;
    activeContracts: ContractApiResponse[];
    canSelectContract: (contractId: string) => boolean;
    paymentMethodType: PaymentMethodType;
    paymentRows: PaymentRow[];
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setSelectedPaymentContracts: Dispatch<SetStateAction<string[]>>;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentDatePickerErrors: Dispatch<SetStateAction<string[]>>;
    getEffectivePaymentAmount: (row: PaymentRow, contract: ContractApiResponse | null | undefined) => number;
    getCardCountryByPaymentMethodId?: (paymentMethodId: string) => string | undefined;
    getTotalAmountForContract: (
        contractId: string,
        allowOutstandingBalanceFallback?: boolean,
    ) => { totalAmount: string } | undefined;
    getScheduledAmountForContract: (contractId: string) => string;
    t: (key: string) => string;
}

/**
 * Owns card-fee lookup/eligibility for the Make Payment page: fetching fee configs, per-contract
 * fee resolution, filtering card-ineligible rows out of `paymentRows`, and per-row fee computation.
 */
export const useCardFees = ({
    userContractsCountryCombinations,
    getContractById,
    activeContracts,
    canSelectContract,
    paymentMethodType,
    paymentRows,
    setPaymentRows,
    setSelectedPaymentContracts,
    setPaymentRowErrors,
    setPaymentDatePickerErrors,
    getEffectivePaymentAmount,
    getCardCountryByPaymentMethodId,
    getTotalAmountForContract,
    t,
}: UseCardFeesParams) => {
    const [cardFees, setCardFees] = useState<FeeResponse[]>([]);
    const [cardAdditionalFees, setCardAdditionalFees] = useState(0);
    const [cardFeesLoading, setCardFeesLoading] = useState(false);
    const [hasFetchedCardFees, setHasFetchedCardFees] = useState(false);
    // True when the card-fees request failed (e.g. endpoint unavailable / network error),
    // as opposed to succeeding with no fees configured. Used to avoid blanking the screen.
    const [cardFeesError, setCardFeesError] = useState(false);

    const fetchCardFees = useCallback(async () => {
        setCardFeesLoading(true);
        setCardFeesError(false);
        try {
            const response = await getPaymentFees(false, userContractsCountryCombinations);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                const additionalFee = Number(response.additionalFees ?? 0);
                setCardAdditionalFees(Number.isFinite(additionalFee) ? additionalFee : 0);
                setCardFees(response.data);
            } else {
                // Non-200 response: the fees service is unavailable, not a legitimate
                // "no fees configured" result. Flag it so we don't blank out the screen.
                setCardFees([]);
                setCardAdditionalFees(0);
                setCardFeesError(true);
            }
        } catch (err) {
            console.error('Error fetching card fees:', err);
            setCardFees([]);
            setCardAdditionalFees(0);
            setCardFeesError(true);
        } finally {
            setCardFeesLoading(false);
            setHasFetchedCardFees(true);
        }
    }, [userContractsCountryCombinations]);

    const getFeeEntriesForContract = useCallback(
        (contractId: string): FeeResponse[] => {
            const contract = getContractById(contractId);
            if (!contract || cardFees.length === 0) return [];

            const contractCountry = String(contract.Country || '').trim();
            const contractState = String(contract.State || '')
                .trim()
                .toLowerCase();

            return cardFees.filter(
                fee =>
                    String(fee.country || '')
                        .trim()
                        .toLowerCase() === contractCountry.toLowerCase() &&
                    String(fee.state || '')
                        .trim()
                        .toLowerCase() === contractState,
            );
        },
        [getContractById, cardFees],
    );

    const getFeeForContract = useCallback(
        (contractId: string): FeeResponse | undefined => {
            const entries = getFeeEntriesForContract(contractId);
            return entries.find(entry => entry.isActive === true);
        },
        [getFeeEntriesForContract],
    );

    const isCardUnavailableForContract = useCallback(
        (contractId: string): boolean => {
            if (paymentMethodType !== 'card') return false;
            if (!hasFetchedCardFees) return false;
            // If the fees request failed, we can't determine availability — don't mark
            // contracts unavailable (which would otherwise remove them and blank the screen).
            if (cardFeesError) return false;

            const contract = getContractById(contractId);
            if (!contract) return true;

            // Credit card payment is only for contracts without AutoPay enabled — AutoPay contracts
            // must use bank transfer instead.
            if (contract.AutoPay) return true;

            // Only Consumer Loan (I + IsLoan) can pay with card
            // All others (Commercial Loan, Commercial Lease, Consumer Lease) cannot pay with card
            if (contract.AccountType !== 'I' || !contract.IsLoan) return true;

            // Only block ACH_ONLY states; CO and NY are now allowed
            const contractState = String(contract.State || '')
                .trim()
                .toUpperCase();
            const stateRule = getStateRule(contractState);
            if (stateRule.mode === 'ACH_ONLY') return true;

            // For card payments: must have open invoices to be eligible
            // Don't allow card payments when there are no open invoices (move to ineligible section)
            const aspireId = contract.AspireId || `Contract ${contract.Id}`;
            const contractData = getTotalAmountForContract(aspireId, false);
            const totalAmount = parseFloat(contractData?.totalAmount || '0');

            // Block if no open invoices (card requires invoices, not just outstanding balance)
            if (totalAmount <= 0) return true;

            const feeObj = getFeeForContract(contractId);
            return !feeObj || feeObj.fees === null || feeObj.isActive !== true;
        },
        [
            paymentMethodType,
            hasFetchedCardFees,
            cardFeesError,
            getFeeForContract,
            getContractById,
            getTotalAmountForContract,
        ],
    );

    const MIN_CARD_FEE = 2.9;

    const isCardBelowMinFeeForContract = useCallback(
        (contractId: string): boolean => {
            if (paymentMethodType !== 'card') return false;
            if (!hasFetchedCardFees || cardFeesError) return false;
            const feeObj = getFeeForContract(contractId);
            if (!feeObj || feeObj.fees === null || feeObj.isActive !== true) return false;
            const feePercent = Number(feeObj.fees);
            return Number.isFinite(feePercent) && feePercent < MIN_CARD_FEE;
        },
        [paymentMethodType, hasFetchedCardFees, cardFeesError, getFeeForContract],
    );

    useEffect(() => {
        if (paymentMethodType !== 'card' || !hasFetchedCardFees || paymentRows.length === 0) return;
        // Don't remove any contracts if the fees request failed — availability is unknown,
        // so keep the contracts on screen rather than blanking it out.
        if (cardFeesError) return;

        const unavailableContractIds = new Set(
            paymentRows
                .filter(
                    row => isCardUnavailableForContract(row.contractId) || isCardBelowMinFeeForContract(row.contractId),
                )
                .map(row => row.contractId),
        );

        if (unavailableContractIds.size === 0) return;

        setPaymentRows(prev => prev.filter(row => !unavailableContractIds.has(row.contractId)));
        setSelectedPaymentContracts(prev => prev.filter(contractId => !unavailableContractIds.has(contractId)));
        setPaymentRowErrors([]);
        setPaymentDatePickerErrors([]);
    }, [
        paymentMethodType,
        hasFetchedCardFees,
        cardFees,
        cardFeesError,
        paymentRows,
        isCardUnavailableForContract,
        isCardBelowMinFeeForContract,
        setPaymentRows,
        setSelectedPaymentContracts,
        setPaymentRowErrors,
        setPaymentDatePickerErrors,
    ]);

    /** Active (non-autopay) contracts that can't be paid by card — either no fee config or below min fee. */
    const cardIneligibleContracts = useMemo(() => {
        if (paymentMethodType !== 'card' || !hasFetchedCardFees || cardFeesError) return [];
        return activeContracts.filter((c: ContractApiResponse) => {
            const id = c.AspireId || `Contract ${c.Id}`;
            return canSelectContract(id) && (isCardUnavailableForContract(id) || isCardBelowMinFeeForContract(id));
        });
    }, [
        paymentMethodType,
        hasFetchedCardFees,
        cardFeesError,
        activeContracts,
        canSelectContract,
        isCardUnavailableForContract,
        isCardBelowMinFeeForContract,
    ]);

    /** Disabled table rows for card-ineligible contracts, shown at the bottom of the payment table. */
    /** Per-row card fee info — computed every render alongside totalAmount (no extra memoization). */
    const cardFeeByRow = paymentRows.map(row => {
        if (paymentMethodType !== 'card') return { fee: 0, charge: 0, isDisabled: false, isMatched: false };
        if (!hasFetchedCardFees) return { fee: 0, charge: 0, isDisabled: false, isMatched: false };
        // Fees request failed: treat fee as unknown (0) but keep the row enabled so the
        // contract stays visible and selectable instead of being marked "not available".
        if (cardFeesError) return { fee: 0, charge: 0, isDisabled: false, isMatched: false };
        const feeObj = getFeeForContract(row.contractId) ?? getFeeForContract(row.dbContractId);
        if (!feeObj) {
            return {
                fee: 0,
                charge: 0,
                isDisabled: true,
                isMatched: false,
                infoMessage: t('makePayment.cardPaymentNotAvailableForLocation'),
            };
        }
        if (feeObj.fees === null) {
            return {
                fee: 0,
                charge: 0,
                isDisabled: true,
                isMatched: true,
                infoMessage: t('makePayment.cardPaymentNotAvailableForLocation'),
            };
        }
        return {
            fee: Number(feeObj.fees),
            charge: Number(feeObj.charge ?? 0),
            isDisabled: false,
            isMatched: true,
        };
    });

    const getRowCardFeeAmount = (row: PaymentRow, rowIndex: number): number => {
        if (paymentMethodType !== 'card') return 0;
        if (cardFeeByRow[rowIndex]?.isDisabled) return 0;

        const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
        const amount = getEffectivePaymentAmount(row, contract ?? undefined);

        if (!Number.isFinite(amount) || amount <= 0) return 0;

        // EXACT FORMULA: T = (x + 0.30) / 0.971
        // Fee = T - x = ((x + 0.30) / 0.971) - x
        const STRIPE_FIXED_DOLLARS = 0.3;
        const STRIPE_NET_RATE = 0.971;
        const totalCharge = (amount + STRIPE_FIXED_DOLLARS) / STRIPE_NET_RATE;
        const calculatedFee = Math.round((totalCharge - amount) * 100) / 100;

        // INTERNATIONAL TRANSACTION SURCHARGES (Ready for future use when international cards are enabled)
        // Currently: Only US cards are allowed, so these surcharges won't apply until international support is enabled.
        // To enable: Remove the country validation in usePaymentSubmission.ts (line 259-269)

        let additionalSurcharge = 0;
        const contractCountry = String(contract?.Country || 'US')
            .trim()
            .toUpperCase();
        const isInternational =
            contractCountry !== 'US' && contractCountry !== 'USA' && contractCountry !== 'UNITED STATES';

        // International contract surcharge: +1.5% for contracts outside the US
        if (isInternational) {
            additionalSurcharge += (amount * 1.5) / 100;
        }

        // Foreign Exchange (FX) surcharge: +1% when card country differs from contract country
        // This applies when international cards are enabled and card country ≠ contract country
        if (getCardCountryByPaymentMethodId && row.paymentCardId) {
            const cardCountry = getCardCountryByPaymentMethodId(row.paymentCardId);
            if (cardCountry) {
                const normalizedCardCountry = String(cardCountry).trim().toUpperCase();
                const isFX =
                    normalizedCardCountry !== 'US' &&
                    normalizedCardCountry !== 'USA' &&
                    normalizedCardCountry !== 'UNITED STATES' &&
                    normalizedCardCountry !== contractCountry;

                if (isFX) {
                    additionalSurcharge += (amount * 1) / 100;
                }
            }
        }

        const additionalFee = Number.isFinite(cardAdditionalFees) ? cardAdditionalFees : 0;
        return Number((calculatedFee + additionalSurcharge + additionalFee).toFixed(2));
    };

    const totalCardFee =
        paymentMethodType === 'card'
            ? paymentRows.reduce((acc, row, rowIndex) => acc + getRowCardFeeAmount(row, rowIndex), 0)
            : 0;

    return {
        cardFees,
        setCardFees,
        cardAdditionalFees,
        setCardAdditionalFees,
        cardFeesLoading,
        setCardFeesLoading,
        hasFetchedCardFees,
        setHasFetchedCardFees,
        cardFeesError,
        setCardFeesError,
        fetchCardFees,
        getFeeEntriesForContract,
        getFeeForContract,
        MIN_CARD_FEE,
        isCardUnavailableForContract,
        isCardBelowMinFeeForContract,
        cardIneligibleContracts,
        cardFeeByRow,
        getRowCardFeeAmount,
        totalCardFee,
    };
};
